import { useState } from 'react'
import { FileText, Loader2, Printer, Copy, Check, Send, BriefcaseMedical, ClipboardCheck, ShieldCheck, Stethoscope, HeartPulse, Scale, FlaskConical, ArrowLeft, ClipboardList } from 'lucide-react'
import { api } from '../../lib/api'

interface LetterField {
  key: string
  label: string
  placeholder?: string
  type?: 'text' | 'textarea' | 'select'
  options?: string[]
}

interface LetterType {
  type: string
  name: string
  description: string
  icon: typeof FileText
  fields: LetterField[]
}

const LETTER_TYPES: LetterType[] = [
  {
    type: 'referral', name: 'Referral Letter', description: 'Professional referral to another specialist', icon: Send,
    fields: [
      { key: 'Referring to', label: 'Referring to (provider / specialty)', placeholder: 'e.g., Dr. Smith, Cardiology' },
      { key: 'Specific question for specialist', label: 'Specific question for the specialist', placeholder: 'e.g., Evaluate for surgical candidacy', type: 'textarea' },
      { key: 'Urgency', label: 'Urgency', type: 'select', options: ['Routine', 'Urgent', 'Emergent'] },
    ],
  },
  {
    type: 'return_to_work', name: 'Return to Work', description: 'Work status and restrictions for employer', icon: BriefcaseMedical,
    fields: [
      { key: 'Work status', label: 'Work status', type: 'select', options: ['Full duty', 'Light duty', 'Off work'] },
      { key: 'Restrictions', label: 'Restrictions', placeholder: 'e.g., No lifting >10 lbs, no overhead work', type: 'textarea' },
      { key: 'Duration / return date', label: 'Duration / return date', placeholder: 'e.g., Through 7/1, re-evaluate at follow-up' },
    ],
  },
  {
    type: 'sick_note', name: 'Sick / Excuse Note', description: 'Brief work or school excuse', icon: Stethoscope,
    fields: [
      { key: 'Excuse from', label: 'Excuse from', type: 'select', options: ['Work', 'School', 'Work and school'] },
      { key: 'Dates excused', label: 'Dates excused', placeholder: 'e.g., 6/11 through 6/14' },
      { key: 'Expected return date', label: 'Expected return date', placeholder: 'e.g., 6/15' },
    ],
  },
  {
    type: 'disability', name: 'Disability Documentation', description: 'Medical documentation for disability claims', icon: ClipboardCheck,
    fields: [
      { key: 'Recipient', label: 'Recipient (employer / insurer / agency)', placeholder: 'e.g., MetLife Disability' },
      { key: 'Functional limitations', label: 'Functional limitations', placeholder: 'e.g., Cannot stand >15 min, no repetitive gripping', type: 'textarea' },
      { key: 'Expected duration', label: 'Expected duration', placeholder: 'e.g., 6-8 weeks, permanent' },
    ],
  },
  {
    type: 'pre_auth', name: 'Pre-Authorization', description: 'Letter of medical necessity for insurance', icon: ShieldCheck,
    fields: [
      { key: 'Insurance company', label: 'Insurance company', placeholder: 'e.g., Aetna' },
      { key: 'Requested procedure / service', label: 'Requested procedure / service', placeholder: 'e.g., MRI right shoulder, arthroscopy' },
      { key: 'Treatments tried and failed', label: 'Treatments tried & failed', placeholder: 'e.g., 6 weeks PT, NSAIDs, corticosteroid injection', type: 'textarea' },
    ],
  },
  {
    type: 'clearance_request', name: 'Request Clearance', description: 'Ask PCP/cardiology to clear your surgical patient', icon: ClipboardList,
    fields: [
      { key: 'Send to', label: 'Send to (PCP / cardiologist)', placeholder: 'e.g., Dr. Patel, Cardiology' },
      { key: 'Planned procedure and date', label: 'Planned procedure & date', placeholder: 'e.g., Right TKA on 7/15/2026' },
      { key: 'Specific management questions', label: 'Specific questions (meds, risk, testing)', placeholder: 'e.g., Clear for surgery; advise on holding aspirin and Eliquis perioperatively', type: 'textarea' },
    ],
  },
  {
    type: 'surgical_clearance', name: 'Surgical Clearance', description: 'Preoperative medical clearance for a surgeon', icon: HeartPulse,
    fields: [
      { key: 'Surgeon', label: 'Surgeon / practice', placeholder: 'e.g., Dr. Lee, Orthopedic Surgery' },
      { key: 'Planned procedure', label: 'Planned procedure', placeholder: 'e.g., Total knee arthroplasty' },
      { key: 'Surgery date', label: 'Surgery date', placeholder: 'e.g., 7/10/2026' },
    ],
  },
  {
    type: 'appeal', name: 'Claim Denial Appeal', description: 'Appeal an insurance claim denial', icon: Scale,
    fields: [
      { key: 'Insurance company', label: 'Insurance company', placeholder: 'e.g., UnitedHealthcare' },
      { key: 'Claim / reference number', label: 'Claim / reference number', placeholder: 'e.g., CLM-104822' },
      { key: 'Stated reason for denial', label: 'Stated reason for denial', placeholder: 'e.g., Not medically necessary', type: 'textarea' },
    ],
  },
  {
    type: 'test_results', name: 'Test Results Letter', description: 'Patient-friendly results summary', icon: FlaskConical,
    fields: [
      { key: 'Test type', label: 'Test type', placeholder: 'e.g., MRI right shoulder, lipid panel' },
      { key: 'Results summary', label: 'Results summary', placeholder: 'e.g., Full-thickness supraspinatus tear', type: 'textarea' },
      { key: 'Recommended follow-up', label: 'Recommended follow-up', placeholder: 'e.g., Discuss surgical options at next visit' },
    ],
  },
]

interface LetterGeneratorProps {
  noteId: string
}

export function LetterGenerator({ noteId }: LetterGeneratorProps) {
  const [selectedType, setSelectedType] = useState<LetterType | null>(null)
  const [fieldValues, setFieldValues] = useState<Record<string, string>>({})
  const [extraInstructions, setExtraInstructions] = useState('')
  const [generating, setGenerating] = useState(false)
  const [generatedLetter, setGeneratedLetter] = useState<string | null>(null)
  const [generatedTypeName, setGeneratedTypeName] = useState<string>('')
  const [error, setError] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)

  function selectType(t: LetterType) {
    setSelectedType(t)
    setFieldValues({})
    setExtraInstructions('')
    setError(null)
  }

  async function handleGenerate() {
    if (!selectedType) return
    setGenerating(true)
    setError(null)

    const ctx: Record<string, string> = {}
    for (const [k, v] of Object.entries(fieldValues)) {
      if (v && v.trim()) ctx[k] = v.trim()
    }
    if (extraInstructions.trim()) ctx['Additional instructions'] = extraInstructions.trim()

    try {
      const data = await api.generateLetter(noteId, selectedType.type, ctx)
      setGeneratedLetter(data.content)
      setGeneratedTypeName(selectedType.name)
    } catch (err: any) {
      setError(err.message)
    } finally {
      setGenerating(false)
    }
  }

  async function handleCopy() {
    if (generatedLetter) {
      await navigator.clipboard.writeText(generatedLetter)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  function handlePrint() {
    if (!generatedLetter) return
    const printWindow = window.open('', '_blank')
    if (printWindow) {
      printWindow.document.write(`
        <html>
          <head>
            <title>${generatedTypeName}</title>
            <style>
              body { font-family: Georgia, 'Times New Roman', serif; padding: 56px 64px; line-height: 1.7; color: #1a1a1a; max-width: 720px; margin: 0 auto; }
              pre { white-space: pre-wrap; font-family: inherit; font-size: 14px; }
            </style>
          </head>
          <body>
            <pre>${generatedLetter}</pre>
          </body>
        </html>
      `)
      printWindow.document.close()
      printWindow.print()
    }
  }

  // ===== Generated letter view =====
  if (generatedLetter) {
    return (
      <div>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <FileText className="w-4 h-4 text-brand-500" />
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{generatedTypeName}</h3>
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleCopy}
              className="px-3 py-1.5 text-sm bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-200 dark:hover:bg-gray-700 flex items-center gap-1.5"
            >
              {copied ? <Check className="w-3.5 h-3.5 text-emerald-500" /> : <Copy className="w-3.5 h-3.5" />}
              {copied ? 'Copied' : 'Copy'}
            </button>
            <button
              onClick={handlePrint}
              className="px-3 py-1.5 text-sm bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-200 dark:hover:bg-gray-700 flex items-center gap-1.5"
            >
              <Printer className="w-3.5 h-3.5" />
              Print / PDF
            </button>
            <button
              onClick={() => {
                setGeneratedLetter(null)
                setSelectedType(null)
              }}
              className="px-3 py-1.5 text-sm bg-brand-600 text-white rounded-md hover:bg-brand-500"
            >
              New Letter
            </button>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm px-10 py-8 max-h-[32rem] overflow-y-auto">
          <pre className="whitespace-pre-wrap text-[15px] leading-7 text-gray-800 dark:text-gray-200" style={{ fontFamily: "Georgia, 'Times New Roman', serif" }}>
            {generatedLetter}
          </pre>
        </div>
        <p className="mt-2 text-xs text-gray-400 dark:text-gray-500">
          Review and edit before sending — bracketed fields need your input.
        </p>
      </div>
    )
  }

  // ===== Requirements form view =====
  if (selectedType) {
    const Icon = selectedType.icon
    return (
      <div>
        <button
          onClick={() => setSelectedType(null)}
          disabled={generating}
          className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 mb-3"
        >
          <ArrowLeft className="w-3.5 h-3.5" />
          All letter types
        </button>

        <div className="flex items-center gap-2 mb-1">
          <Icon className="w-4 h-4 text-brand-500" />
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{selectedType.name}</h3>
        </div>
        <p className="text-xs text-gray-400 dark:text-gray-500 mb-4">
          Everything is optional — the letter is built from your note. Fill in what you want it to say.
        </p>

        {error && (
          <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
            <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
          </div>
        )}

        <div className="space-y-3 max-w-xl">
          {selectedType.fields.map((field) => (
            <div key={field.key}>
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{field.label}</label>
              {field.type === 'select' ? (
                <select
                  value={fieldValues[field.key] || ''}
                  onChange={(e) => setFieldValues((p) => ({ ...p, [field.key]: e.target.value }))}
                  className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-brand-500"
                >
                  <option value="">—</option>
                  {field.options?.map((o) => (
                    <option key={o} value={o}>{o}</option>
                  ))}
                </select>
              ) : field.type === 'textarea' ? (
                <textarea
                  value={fieldValues[field.key] || ''}
                  onChange={(e) => setFieldValues((p) => ({ ...p, [field.key]: e.target.value }))}
                  placeholder={field.placeholder}
                  rows={2}
                  className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-brand-500 resize-y"
                />
              ) : (
                <input
                  type="text"
                  value={fieldValues[field.key] || ''}
                  onChange={(e) => setFieldValues((p) => ({ ...p, [field.key]: e.target.value }))}
                  placeholder={field.placeholder}
                  className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-brand-500"
                />
              )}
            </div>
          ))}

          <div>
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Anything else the letter should say?</label>
            <textarea
              value={extraInstructions}
              onChange={(e) => setExtraInstructions(e.target.value)}
              placeholder="Free-form instructions — tone, points to emphasize, things to leave out…"
              rows={2}
              className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-brand-500 resize-y"
            />
          </div>

          <button
            onClick={handleGenerate}
            disabled={generating}
            className="w-full sm:w-auto px-5 py-2.5 bg-brand-600 hover:bg-brand-500 text-white text-sm font-medium rounded-lg flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {generating ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Generating from your note…
              </>
            ) : (
              <>Generate {selectedType.name}</>
            )}
          </button>
        </div>
      </div>
    )
  }

  // ===== Type picker view =====
  return (
    <div>
      <div className="flex items-center gap-2 mb-1">
        <FileText className="w-4 h-4 text-brand-500" />
        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Post-Visit Letters</h3>
      </div>
      <p className="text-xs text-gray-400 dark:text-gray-500 mb-4">
        Generated from this note — no re-dictating.
      </p>

      {error && (
        <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
          <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {LETTER_TYPES.map((type) => {
          const Icon = type.icon
          return (
            <button
              key={type.type}
              onClick={() => selectType(type)}
              className="text-left p-4 rounded-lg border transition-all border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 hover:border-brand-400 hover:shadow-sm"
            >
              <div className="flex items-center gap-2.5 mb-1">
                <Icon className="w-4 h-4 text-brand-500" />
                <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{type.name}</span>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed pl-[26px]">
                {type.description}
              </p>
            </button>
          )
        })}
      </div>
    </div>
  )
}
